import BoltRoundedIcon from '@mui/icons-material/BoltRounded'
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded'
import DynamicFormRoundedIcon from '@mui/icons-material/DynamicFormRounded'
import HubOutlinedIcon from '@mui/icons-material/HubOutlined'
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded'
import PaidRoundedIcon from '@mui/icons-material/PaidRounded'
import VideoCameraFrontRoundedIcon from '@mui/icons-material/VideoCameraFrontRounded'
import { type ReactNode } from 'react'
import type { TimelineElement } from '../../../context/TimelineContext'
import type { Workspace, WorkspaceRole } from '../../../context/WorkspaceContext'
import { useNls } from '../../../nls/nlsProvider'
import type { AnalysisIncludedSystemKey, AnalysisRecommendation, AnalysisSnapshot, AnalysisTimeRangeKey } from '../../../types/analysis'
import { Button } from '../../Common/Button'
import { RailPageShellCard } from '../../Common/RailPageShell'
import { MetricCard } from '../MetricCard'

import { isSparklineAnomaly, LiveModuleCard, useAutomationHealthModule, useIntegrationHealthModule, useMeetingsReadinessModule, useUploadProofBacklogModule } from '../modules'
import { buildLiveModuleDescriptor, buildMetricDescriptor } from '../popover/descriptors'

import { formatNumber } from './analysisTabShared'
import type { AnalysisNavigate } from './analysisTabShared'

export const OperationsTab = ({
  workspace,
  role,
  timeRange,
  elements,
  snapshot,
  snapshotNonce,
  onNavigate,
  onOpenReports,
  includedSystems,
  highlightAnomalies,
  memberDense = false,
}: {
  workspace: Workspace
  role: WorkspaceRole
  timeRange: AnalysisTimeRangeKey
  elements: TimelineElement[]
  snapshot: AnalysisSnapshot
  snapshotNonce: number
  onNavigate: AnalysisNavigate
  onOpenReports: () => void
  includedSystems?: Set<AnalysisIncludedSystemKey>
  highlightAnomalies?: boolean
  memberDense?: boolean
}) => {
  const { t } = useNls()
  void role
  const automationHealth = useAutomationHealthModule({
    workspaceId: workspace.id,
    timeRange,
    refreshKey: snapshotNonce,
  })
  const meetingsReadiness = useMeetingsReadinessModule({
    workspaceId: workspace.id,
    timeRange,
    refreshKey: snapshotNonce,
  })
  const uploadProofBacklog = useUploadProofBacklogModule({
    workspaceId: workspace.id,
    snapshot,
    elements,
    timeRange,
    refreshKey: snapshotNonce,
  })
  const integrationHealth = useIntegrationHealthModule({
    workspaceId: workspace.id,
    timeRange,
    refreshKey: snapshotNonce,
  })
  const showAutomationHealth = !includedSystems || includedSystems.has('AUTOMATIONS')
  const showMeetingsReadiness = !includedSystems || includedSystems.has('MEETINGS')
  const showUploadProofBacklog = !includedSystems || includedSystems.has('UPLOADS')
  const showIntegrationHealth = !includedSystems || includedSystems.has('INTEGRATIONS')
  const visibleModuleCount = [showAutomationHealth, showMeetingsReadiness, showUploadProofBacklog, showIntegrationHealth].filter(Boolean).length
  const phaseGridClass = visibleModuleCount >= 4 ? 'analysisPhaseGrid analysisPhaseGrid--four' : visibleModuleCount === 2 ? 'analysisPhaseGrid analysisPhaseGrid--two' : 'analysisPhaseGrid'
  const opsMetrics: Array<{
    system: AnalysisIncludedSystemKey
    title: string
    value: string
    detail: string
    icon: ReactNode
    numeric: number
    drillTarget: AnalysisRecommendation['drillTarget'] | null
  }> = [
    {
      system: 'MEETINGS',
      title: 'Meetings analysis',
      value: 'Ready',
      detail: 'Open Meetings for provider and readiness details',
      icon: <VideoCameraFrontRoundedIcon />,
      numeric: 0,
      drillTarget: 'MEETINGS',
    },
    {
      system: 'UPLOADS',
      title: 'Uploads and proof',
      value: formatNumber(snapshot.ops.proofRequired),
      detail: 'Proof-gated records in active work',
      icon: <CloudUploadRoundedIcon />,
      numeric: snapshot.ops.proofRequired,
      drillTarget: 'UPLOAD',
    },
    {
      system: 'AUTOMATIONS',
      title: 'Automation health',
      value: 'Local',
      detail: 'Live run history is available in the module below.',
      icon: <BoltRoundedIcon />,
      numeric: 0,
      drillTarget: 'AUTOMATE',
    },
    {
      system: 'BUDGET',
      title: 'Budget analysis',
      value: formatNumber(snapshot.ops.budgeted),
      detail: 'Budget-touched active records',
      icon: <PaidRoundedIcon />,
      numeric: snapshot.ops.budgeted,
      drillTarget: 'BUDGET',
    },
    {
      system: 'STOCK',
      title: 'Stock analysis',
      value: formatNumber(snapshot.ops.stockTouched),
      detail: 'Records with stock requirements',
      icon: <Inventory2RoundedIcon />,
      numeric: snapshot.ops.stockTouched,
      drillTarget: 'STOCK',
    },
    {
      system: 'FORMS',
      title: 'Forms analysis',
      value: formatNumber(snapshot.ops.formsTouched),
      detail: 'Records linked to Forms',
      icon: <DynamicFormRoundedIcon />,
      numeric: snapshot.ops.formsTouched,
      drillTarget: null,
    },
    {
      system: 'INTEGRATIONS',
      title: 'Integration health',
      value: formatNumber(snapshot.ops.integrationsTouched),
      detail: 'Records with provider mappings',
      icon: <HubOutlinedIcon />,
      numeric: snapshot.ops.integrationsTouched,
      drillTarget: 'LINK',
    },
  ]
  const visibleMetrics = includedSystems ? opsMetrics.filter((m) => includedSystems.has(m.system)) : opsMetrics
  const memberEmptyState = memberDense
    ? { heading: t('NLSAnalysisMobileNoSignalsHeading'), detail: t('NLSAnalysisMobileNoSignalsDetail') }
    : undefined

  if (includedSystems && visibleMetrics.length === 0) {
    return (
      <div className="analysisFilteredStateEmpty" data-testid="operations-filtered-empty">
        <strong>{t('NLSAnalysisFilteredEmptyHeading')}</strong>
        <p>{t('NLSAnalysisFilteredEmptyDesc')}</p>
      </div>
    )
  }

  return (
    <div className="analysisStageStack">
      <div className={`analysisModuleGrid${memberDense ? ' analysisModuleGrid--memberDense' : ''}`}>
        {visibleMetrics.map((metric) => (
          <MetricCard
            key={metric.title}
            title={metric.title}
            value={metric.value}
            detail={metric.detail}
            icon={metric.icon}
            descriptor={buildMetricDescriptor({
              snapshot,
              tab: 'OPERATIONS',
              title: metric.title,
              value: metric.numeric,
              detail: metric.detail,
              drillTarget: metric.drillTarget,
              onNavigate,
              onOpenReports,
            })}
          />
        ))}
        <RailPageShellCard className="analysisSetupCard analysisSetupCard--source" heading="Need source-specific detail?" eyebrow="Source detail">
          <p>Phase 1 summarizes local workspace records. Open the owning hub for authoritative provider data.</p>
          <div>
            <Button variant="tertiary" size="sm" onClick={() => onNavigate('MEETINGS')}>
              Meetings
            </Button>
            <Button variant="tertiary" size="sm" onClick={() => onNavigate('LINK')}>
              Link Hub
            </Button>
          </div>
        </RailPageShellCard>
      </div>
      {visibleModuleCount > 0 && (
        <div className={phaseGridClass}>
          {showAutomationHealth && (
            <LiveModuleCard
              data-testid="live-module-automation-health"
              icon={<BoltRoundedIcon />}
              title={t('NLSAnalysisModuleAutomationHealthTitle')}
              eyebrow={t('NLSAnalysisLiveModuleEyebrowOperations')}
              headline={automationHealth.headline}
              sparkline={automationHealth.sparkline}
              records={automationHealth.records}
              emptyState={memberEmptyState}
              loading={automationHealth.loading}
              highlighted={highlightAnomalies && isSparklineAnomaly(automationHealth.sparkline.points)}
              onRecordClick={() => onNavigate('AUTOMATE')}
              descriptor={buildLiveModuleDescriptor({
                snapshot,
                id: `automation-health-${workspace.id}`,
                title: 'Automation health',
                tab: 'OPERATIONS',
                cardLabel: 'Automation health',
                points: automationHealth.sparkline.points,
                records: automationHealth.records.map((record) => ({
                  ...record,
                  drillTarget: 'AUTOMATE',
                })),
                direction: automationHealth.sparkline.direction,
                onNavigate,
                onOpenReports,
              })}
            />
          )}
          {showMeetingsReadiness && (
            <LiveModuleCard
              data-testid="live-module-meetings-readiness"
              icon={<VideoCameraFrontRoundedIcon />}
              title={t('NLSAnalysisModuleMeetingsReadinessTitle')}
              eyebrow={t('NLSAnalysisLiveModuleEyebrowOperations')}
              headline={meetingsReadiness.headline}
              sparkline={meetingsReadiness.sparkline}
              records={meetingsReadiness.records}
              emptyState={memberEmptyState}
              loading={meetingsReadiness.loading}
              highlighted={highlightAnomalies && isSparklineAnomaly(meetingsReadiness.sparkline.points)}
              onRecordClick={() => onNavigate('MEETINGS')}
              descriptor={buildLiveModuleDescriptor({
                snapshot,
                id: `meetings-readiness-${workspace.id}`,
                title: 'Meetings readiness',
                tab: 'OPERATIONS',
                cardLabel: 'Meetings readiness',
                points: meetingsReadiness.sparkline.points,
                records: meetingsReadiness.records.map((record) => ({
                  ...record,
                  drillTarget: 'MEETINGS',
                })),
                direction: meetingsReadiness.sparkline.direction,
                onNavigate,
                onOpenReports,
              })}
            />
          )}
          {showUploadProofBacklog && (
            <LiveModuleCard
              data-testid="live-module-upload-proof-backlog"
              icon={<CloudUploadRoundedIcon />}
              title={t('NLSAnalysisModuleUploadProofBacklogTitle')}
              eyebrow={t('NLSAnalysisLiveModuleEyebrowOperations')}
              headline={uploadProofBacklog.headline}
              sparkline={uploadProofBacklog.sparkline}
              records={uploadProofBacklog.records}
              emptyState={memberEmptyState}
              loading={uploadProofBacklog.loading}
              highlighted={highlightAnomalies && isSparklineAnomaly(uploadProofBacklog.sparkline.points)}
              onRecordClick={() => onNavigate('UPLOAD')}
              descriptor={buildLiveModuleDescriptor({
                snapshot,
                id: `upload-proof-backlog-${workspace.id}`,
                title: 'Upload proof backlog',
                tab: 'OPERATIONS',
                cardLabel: 'Upload proof backlog',
                points: uploadProofBacklog.sparkline.points,
                records: uploadProofBacklog.records.map((record) => ({
                  ...record,
                  drillTarget: 'UPLOAD',
                })),
                direction: uploadProofBacklog.sparkline.direction,
                onNavigate,
                onOpenReports,
              })}
            />
          )}
          {showIntegrationHealth && (
            <LiveModuleCard
              data-testid="live-module-integration-health"
              icon={<HubOutlinedIcon />}
              title={t('NLSAnalysisModuleIntegrationHealthTitle')}
              eyebrow={t('NLSAnalysisLiveModuleEyebrowOperations')}
              headline={integrationHealth.headline}
              sparkline={integrationHealth.sparkline}
              records={integrationHealth.records}
              emptyState={memberEmptyState}
              loading={integrationHealth.loading}
              highlighted={highlightAnomalies && isSparklineAnomaly(integrationHealth.sparkline.points)}
              onRecordClick={() => onNavigate('LINK')}
              descriptor={buildLiveModuleDescriptor({
                snapshot,
                id: `integration-health-${workspace.id}`,
                title: 'Integration health',
                tab: 'OPERATIONS',
                cardLabel: 'Integration health',
                points: integrationHealth.sparkline.points,
                records: integrationHealth.records.map((record) => ({
                  ...record,
                  drillTarget: 'LINK',
                })),
                direction: integrationHealth.sparkline.direction,
                onNavigate,
                onOpenReports,
              })}
            />
          )}
        </div>
      )}
    </div>
  )
}
